import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { HiX, HiChevronLeft, HiChevronRight, HiCheck } from 'react-icons/hi'
import { Button } from 'components/ui'
import { ONBOARDING_STEP_CONFIG, TOTAL_ONBOARDING_STEPS } from 'constants/onboarding.constant'
import { mediatorDataSelector } from 'store/auth/userSlice'
import classNames from 'classnames'

const OnboardingTour = ({
    isActive,
    currentStep,
    pendingStep,
    mediatorId,
    onComplete,
    onSkip,
    onStepChange
}) => {
    const [highlightedElement, setHighlightedElement] = useState(null)
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
    const [tooltipPlacement, setTooltipPlacement] = useState('bottom')
    const [retryCount, setRetryCount] = useState(0)
    const [currentSubStep, setCurrentSubStep] = useState(0)
    const [viewportSize, setViewportSize] = useState(() =>
        typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : { w: 1920, h: 1080 }
    )
    const overlayRef = useRef(null)
    const tooltipRef = useRef(null)
    const retryTimeoutRef = useRef(null)
    const stepConfig = ONBOARDING_STEP_CONFIG[currentStep]

    // Effective element selector and content (for steps with subSteps, use current sub-step)
    const effectiveSelector = stepConfig?.subSteps?.[currentSubStep]
        ? stepConfig.subSteps[currentSubStep].element
        : stepConfig?.element
    const effectiveContent = stepConfig?.subSteps?.[currentSubStep]
        ? stepConfig.subSteps[currentSubStep].content
        : stepConfig?.content

    // Reset sub-step when main step changes
    useEffect(() => {
        setCurrentSubStep(0)
    }, [currentStep])
    const navigate = useNavigate()
    const mediatorData = useSelector(mediatorDataSelector)
    const mediationTime = mediatorData?.mediationTime
    const isMediationTimeComplete =
        !!mediationTime?.timezone &&
        !!mediationTime?.morning_time &&
        !!mediationTime?.afternoon_time &&
        !!mediationTime?.fullday_time

    useEffect(() => {
        const update = () => setViewportSize({ w: window.innerWidth, h: window.innerHeight })
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [])

    // Lock body scroll while tour is active (standard modal behavior)
    useEffect(() => {
        if (!isActive) return
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = prevOverflow
        }
    }, [isActive])

    // Element-based: find target by selector only. Position (rect) is used only for
    // overlay cutout and tooltip placement, and is kept in sync via scroll/resize/ResizeObserver.
    // When step has subSteps, selector is the effective sub-step element selector.
    const calculatePositions = useCallback((selector) => {
        const elSelector = selector || stepConfig?.element
        if (!elSelector) return

        const element = document.querySelector(elSelector)
        if (!element) {
            console.warn(`Element not found: ${elSelector}`)
            return
        }

        const rect = element.getBoundingClientRect()
        // Only treat as "fully rendered" when element has dimensions (e.g. Profile nav visible)
        if (rect.width <= 0 || rect.height <= 0) return

        const scrollY = window.scrollY
        const scrollX = window.scrollX

        setHighlightedElement({
            element,
            rect: {
                top: rect.top + scrollY,
                left: rect.left + scrollX,
                width: rect.width,
                height: rect.height,
            },
            viewportRect: {
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            },
        })

        // Tooltip in viewport coords (overlay is fixed) so it stays fully visible when scroll is locked
        const tooltipWidth = Math.min(420, window.innerWidth - 40)
        const tooltipHeight = Math.min(280, window.innerHeight - 80)
        const padding = 20
        const preferredPosition = stepConfig.position || 'bottom'
        const positions = [preferredPosition]
        if (preferredPosition === 'bottom') positions.push('top', 'right', 'left')
        else if (preferredPosition === 'top') positions.push('bottom', 'right', 'left')
        else if (preferredPosition === 'left') positions.push('right', 'bottom', 'top')
        else positions.push('left', 'bottom', 'top')

        let bestPlacement = preferredPosition
        let bestTop = 0
        let bestLeft = 0
        let bestScore = -1

        for (const placement of positions) {
            let top = 0
            let left = 0
            switch (placement) {
                case 'top':
                    top = rect.top - tooltipHeight - padding
                    left = rect.left + rect.width / 2 - tooltipWidth / 2
                    break
                case 'bottom':
                    top = rect.bottom + padding
                    left = rect.left + rect.width / 2 - tooltipWidth / 2
                    break
                case 'left':
                    top = rect.top + rect.height / 2 - tooltipHeight / 2
                    left = rect.left - tooltipWidth - padding
                    break
                case 'right':
                    top = rect.top + rect.height / 2 - tooltipHeight / 2
                    left = rect.right + padding
                    break
                default:
                    top = rect.bottom + padding
                    left = rect.left + rect.width / 2 - tooltipWidth / 2
            }
            const clampedLeft = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding))
            const clampedTop = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding))
            const inView = clampedLeft === left && clampedTop === top
            const score = inView ? 2 : 1
            if (score > bestScore) {
                bestScore = score
                bestPlacement = placement
                bestTop = clampedTop
                bestLeft = clampedLeft
            }
        }

        setTooltipPlacement(bestPlacement)
        setTooltipPosition({ top: bestTop, left: bestLeft })
    }, [stepConfig])

    useEffect(() => {
        if (isActive && stepConfig && effectiveSelector) {
            // Clear any existing retry timeout
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current)
            }

            const tryFindElement = () => {
                calculatePositions(effectiveSelector)

                // If element not found and we haven't retried too many times, retry
                const element = document.querySelector(effectiveSelector)
                if (!element && retryCount < 10) {
                    setRetryCount(prev => prev + 1)
                    retryTimeoutRef.current = setTimeout(tryFindElement, 300)
                } else if (element) {
                    // Element found, reset retry count
                    setRetryCount(0)
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
            }

            // Initial attempt after a short delay
            retryTimeoutRef.current = setTimeout(tryFindElement, 100)

            // Keep target and tooltip both visible on screen size change or scroll (all steps)
            const ensureTargetInViewThenRecalc = () => {
                const element = document.querySelector(effectiveSelector)
                if (!element) {
                    calculatePositions(effectiveSelector)
                    return
                }
                const rect = element.getBoundingClientRect()
                const vw = window.innerWidth
                const vh = window.innerHeight
                const outOfView =
                    rect.top < 0 ||
                    rect.bottom > vh ||
                    rect.left < 0 ||
                    rect.right > vw
                if (outOfView) {
                    element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' })
                }
                calculatePositions(effectiveSelector)
            }

            let resizeTimer = null
            const handleResize = () => {
                if (resizeTimer) clearTimeout(resizeTimer)
                resizeTimer = setTimeout(ensureTargetInViewThenRecalc, 100)
            }
            const handleScroll = () => ensureTargetInViewThenRecalc()

            window.addEventListener('resize', handleResize)
            window.addEventListener('scroll', handleScroll, true)

            return () => {
                if (resizeTimer) clearTimeout(resizeTimer)
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current)
                }
                window.removeEventListener('resize', handleResize)
                window.removeEventListener('scroll', handleScroll, true)
            }
        } else {
            // Reset retry count when tour is not active
            setRetryCount(0)
        }
    }, [isActive, stepConfig, effectiveSelector, calculatePositions])

    // When we have the target element, observe it so cutout/tooltip stay in sync when it resizes or moves.
    // Use effectiveSelector so sub-steps (e.g. step 4) recalc for the current target, not the fallback.
    useEffect(() => {
        const el = highlightedElement?.element
        if (!el || typeof ResizeObserver === 'undefined' || !stepConfig || !effectiveSelector) return

        let rafId = null
        const onTargetResize = () => {
            if (rafId) cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(() => {
                rafId = null
                const rect = el.getBoundingClientRect()
                const vw = window.innerWidth
                const vh = window.innerHeight
                const outOfView =
                    rect.top < 0 ||
                    rect.bottom > vh ||
                    rect.left < 0 ||
                    rect.right > vw
                if (outOfView) {
                    el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' })
                }
                calculatePositions(effectiveSelector)
            })
        }
        const ro = new ResizeObserver(onTargetResize)
        ro.observe(el)
        return () => {
            if (rafId) cancelAnimationFrame(rafId)
            ro.unobserve(el)
        }
    }, [highlightedElement?.element, stepConfig, effectiveSelector, calculatePositions])

    const handleNext = async () => {
        const hasSubSteps = stepConfig?.subSteps?.length > 0
        if (hasSubSteps && currentSubStep < stepConfig.subSteps.length - 1) {
            setCurrentSubStep((s) => s + 1)
        } else if (currentStep < TOTAL_ONBOARDING_STEPS) {
            const nextStep = currentStep + 1
            onStepChange(nextStep)
        } else {
            await handleComplete()
        }
    }

    const handlePrevious = async () => {
        const hasSubSteps = stepConfig?.subSteps?.length > 0
        if (hasSubSteps && currentSubStep > 0) {
            setCurrentSubStep((s) => s - 1)
        } else if (currentStep > 1) {
            const prevStep = currentStep - 1
            onStepChange(prevStep)
        }
    }

    const handleComplete = async () => {
        // Perform this step's action (e.g. navigate to route) then complete
        if (stepConfig?.route) {
            navigate(stepConfig.route)
        }
        onComplete()
    }

    const handleSkip = async () => {
        onSkip()
    }

    if (!isActive) {
        return null
    }

    // Transitioning to next step: navigate first, then wait for element. Show loading until pendingStep clears.
    if (pendingStep != null) {
        return createPortal(
            <div
                className="fixed inset-0 z-[10001] flex items-center justify-center"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="absolute inset-0"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }}
                />
                <div
                    data-onboarding-tooltip="true"
                    className="relative z-10 bg-white rounded-2xl shadow-2xl px-6 py-4 text-gray-700"
                >
                    Loading next step…
                </div>
            </div>,
            document.body
        )
    }

    if (!stepConfig) {
        return null
    }

    // Step 1: only show popup when Profile nav is found and fully rendered
    if (currentStep === 1 && !highlightedElement) {
        return null
    }

    // If element not found yet, show tooltip in center of screen
    const defaultPosition = highlightedElement ? highlightedElement.rect : {
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 200,
        width: 400,
        height: 200,
    }

    const defaultTooltipPosition = highlightedElement ? tooltipPosition : {
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - 200,
    }

    const isFirstStep = currentStep === 1
    const isLastStep = currentStep === TOTAL_ONBOARDING_STEPS
    const hasMoreSubSteps = stepConfig?.subSteps && currentSubStep < stepConfig.subSteps.length - 1
    const showCompleteButton = isLastStep && !hasMoreSubSteps

    return createPortal(
        <div
            className="fixed inset-0 z-[10001]"
            style={{ pointerEvents: 'none' }}
            onClick={(e) => {
                const isTooltip = e.target.closest('[data-onboarding-tooltip]')
                const isHighlighted = effectiveSelector && e.target.closest(effectiveSelector)
                const allowClick = isTooltip || (isHighlighted && !stepConfig?.blockTarget)
                if (!allowClick) {
                    e.stopPropagation()
                    e.preventDefault()
                }
            }}
        >
            {/* Full-screen dim: four panels leave a hole so the highlight is visible. Invisible blocker over the hole prevents clicking the highlighted element (e.g. Profile); user advances via Next/Complete only. */}
            {(() => {
                const dimStyle = { backgroundColor: 'rgba(0, 0, 0, 0.55)', pointerEvents: 'auto' }
                const vr = highlightedElement?.viewportRect
                if (!vr) {
                    return <div ref={overlayRef} className="absolute inset-0 w-full h-full" style={dimStyle} />
                }
                const pad = (stepConfig?.step >= 2 && stepConfig?.step <= 4) ? 12 : 2
                const holeLeft = Math.floor(vr.left) - pad
                const holeTop = Math.floor(vr.top) - pad
                const holeW = Math.ceil(vr.width) + pad * 2
                const holeH = Math.ceil(vr.height) + pad * 2
                const bottomPanelTop = holeTop + holeH
                const bottomPanelHeight = Math.max(0, Math.floor(viewportSize.h - bottomPanelTop))
                return (
                    <>
                        <div ref={overlayRef} className="absolute left-0 top-0 w-full" style={{ ...dimStyle, height: Math.max(0, holeTop) }} />
                        <div className="absolute left-0 top-0" style={{ ...dimStyle, top: holeTop, width: Math.max(0, holeLeft), height: holeH }} />
                        <div className="absolute top-0" style={{ ...dimStyle, left: holeLeft + holeW, top: holeTop, width: Math.max(0, Math.floor(viewportSize.w - (holeLeft + holeW))), height: holeH }} />
                        <div className="absolute left-0 w-full" style={{ ...dimStyle, top: bottomPanelTop, height: bottomPanelHeight }} />
                    </>
                )
            })()}

            {/* Invisible block over the highlight when step config says blockTarget (e.g. step 1 Profile) */}
            {highlightedElement?.viewportRect && stepConfig?.blockTarget && (
                <div
                    className="absolute pointer-events-auto"
                    style={{
                        left: highlightedElement.viewportRect.left,
                        top: highlightedElement.viewportRect.top,
                        width: highlightedElement.viewportRect.width,
                        height: highlightedElement.viewportRect.height,
                    }}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                    }}
                    aria-hidden
                />
            )}

            {/* Ring applied directly to the target component (no position-based ring) */}
            {effectiveSelector && (
                <style>{`
                    ${effectiveSelector} {
                        box-shadow: 0 0 0 4px rgba(15, 94, 89, 0.5), 0 0 0 8px rgba(15, 94, 89, 0.2);
                        border-radius: 12px;
                        position: relative;
                        z-index: 10000;
                    }
                `}</style>
            )}

            {/* Consolidated animations */}
            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
            `}</style>

            {/* Professional Tooltip with smooth animations - overflow visible so arrow is not clipped */}
            <div
                ref={tooltipRef}
                data-onboarding-tooltip="true"
                className="absolute bg-white rounded-2xl shadow-2xl pointer-events-auto border border-gray-100"
                style={{
                    top: defaultTooltipPosition.top,
                    left: defaultTooltipPosition.left,
                    width: '420px',
                    maxWidth: 'min(420px, calc(100vw - 40px))',
                    overflow: 'visible',
                    zIndex: 10002,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                    animation: 'fadeInUp 0.4s ease-out',
                    transition: 'top 0.25s ease-out, left 0.25s ease-out',
                }}
                onClick={(e) => {
                    // Allow all clicks within tooltip
                    e.stopPropagation()
                }}
            >
                {/* Tooltip Arrow - dynamic placement: above tooltip when below target, below when above target, etc. */}
                {highlightedElement && (
                    <div
                        className={classNames(
                            'absolute w-0 h-0',
                            tooltipPlacement === 'bottom' && 'bottom-full left-1/2 -translate-x-1/2 border-l-[14px] border-r-[14px] border-b-[14px] border-b-white border-l-transparent border-r-transparent drop-shadow-lg',
                            tooltipPlacement === 'top' && 'top-full left-1/2 -translate-x-1/2 border-l-[14px] border-r-[14px] border-t-[14px] border-t-white border-l-transparent border-r-transparent drop-shadow-lg',
                            tooltipPlacement === 'left' && 'left-full top-1/2 -translate-y-1/2 border-t-[14px] border-b-[14px] border-l-[14px] border-l-white border-t-transparent border-b-transparent drop-shadow-lg',
                            tooltipPlacement === 'right' && 'right-full top-1/2 -translate-y-1/2 border-t-[14px] border-b-[14px] border-r-[14px] border-r-white border-t-transparent border-b-transparent drop-shadow-lg'
                        )}
                    />
                )}

                {/* Tooltip: header fixed, content scrollable, footer fixed so buttons are always visible */}
                <div
                    className="flex flex-col p-5"
                    style={{
                        maxHeight: 'min(360px, calc(100vh - 80px))',
                    }}
                >
                    {/* Header - always visible */}
                    <div className="flex-shrink-0 flex items-start justify-between pb-4 border-b border-gray-100">
                        <div className="flex-1">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-12 h-12 bg-teal-800 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-teal-100">
                                    <span className="text-white font-bold text-base">{currentStep}</span>
                                </div>
                                <div className="flex-1 pt-0.5">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
                                        {stepConfig.title}
                                    </h3>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="text-xs font-semibold text-teal-800 uppercase tracking-wider">
                                            Step {currentStep} of {TOTAL_ONBOARDING_STEPS}
                                        </span>
                                        <div className="flex gap-1.5 items-center">
                                            {Array.from({ length: TOTAL_ONBOARDING_STEPS }).map((_, idx) => (
                                                <div
                                                    key={idx}
                                                    className={classNames(
                                                        'h-2 w-2 rounded-full transition-all duration-300',
                                                        idx + 1 <= currentStep
                                                            ? 'bg-[#0f5e59] shadow-sm ring-2 ring-[#0f5e59]'
                                                            : 'bg-gray-200'
                                                    )}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handleSkip}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all rounded-lg p-2 -mt-1 -mr-1"
                            aria-label="Close"
                        >
                            <HiX className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content - scrollable only this part */}
                    <div
                        className="flex-1 min-h-0 overflow-y-auto"
                        style={{ overflowX: 'hidden' }}
                    >
                        <p className="text-gray-700 text-[15px] leading-relaxed font-normal pr-1 py-1">
                            {effectiveContent}
                        </p>
                    </div>

                    {/* Footer - always visible */}
                    <div className="flex-shrink-0 flex items-center justify-between pt-2 mt-1 border-t border-gray-100">
                        <div className="flex gap-2">
                            {/* Previous button commented out for step 2 and beyond */}
                            {/* {!isFirstStep && (
                                <Button
                                    variant="plain"
                                    size="sm"
                                    onClick={handlePrevious}
                                    className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 px-4 py-2.5 rounded-lg transition-all font-medium border border-gray-200 hover:border-gray-300"
                                >
                                    <HiChevronLeft className="w-4 h-4 mr-1.5" />
                                    Previous
                                </Button>
                            )} */}
                        </div>

                        <div className="flex gap-3">
                            <Button
                                variant="plain"
                                size="sm"
                                onClick={handleSkip}
                                className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 px-4 py-2.5 rounded-lg font-medium transition-all border border-gray-200 hover:border-gray-300 flex items-center justify-center"
                            >
                                Skip Tour
                            </Button>
                            <Button
                                variant="solid"
                                size="sm"
                                onClick={showCompleteButton ? handleComplete : handleNext}
                                disabled={showCompleteButton && !isMediationTimeComplete}
                                title={showCompleteButton && !isMediationTimeComplete ? 'Complete your mediation time' : undefined}
                                className={classNames(
                                    'px-5 py-2 rounded-md font-semibold shadow-sm transition-all duration-200 flex items-center justify-center',
                                    showCompleteButton && !isMediationTimeComplete
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-teal-800 hover:bg-teal-700 text-white hover:shadow-md'
                                )}
                            >
                                {showCompleteButton ? (
                                    <span className="flex items-center justify-center">
                                        <HiCheck className="w-4 h-4 mr-1.5" />
                                        Complete
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center">
                                        Next
                                        <HiChevronRight className="w-4 h-4 ml-1.5" />
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default OnboardingTour
